import React from 'react';
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { PeriodType } from '@shopify-multiseller/graphql/partner/types';

type DataPoint = {
  key: number | string;
  value: number;
};

type ChartData = {
  values: DataPoint[];
  quantity: number;
  total: number;
};

export interface ChartProps {
  title: string;
  current: ChartData;
  previous: ChartData;
  period: PeriodType;
}

/**
 * Formats an x-axis key for display. Keys are millisecond timestamps in
 * production, but can be arbitrary labels in mocks/edge data — so we fall back
 * to the raw key string rather than rendering "Invalid Date".
 */
const formatKey = (key: number | string, period: PeriodType): string => {
  const raw = String(key);
  if (raw.trim() === '') return raw;

  const timestamp = typeof key === 'number' ? key : Number(key);
  if (!Number.isFinite(timestamp)) return raw;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return raw;

  switch (period) {
    case PeriodType.Today:
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case PeriodType.Last_90Days:
    case PeriodType.Last_180Days:
    case PeriodType.Last_365Days:
      return date.toLocaleDateString([], { month: 'short' });
    default:
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
};

/**
 * The series names. The period is already stated by the selector and by the
 * card's own subtitle, so the legend says which line is which and no more —
 * "This Month vs Previous Month" beside a 30-day range was restating it a
 * third time, in different words.
 */
const periodLabels = (period: PeriodType): { current: string; previous: string } =>
  period === PeriodType.Today
    ? { current: 'Today', previous: 'Yesterday' }
    : { current: 'This period', previous: 'Previous' };

/**
 * The last point on the current series, called out with its value.
 *
 * The design draws the plot without a value axis and instead marks where the
 * line ends and says what it reached. That is the number a seller is looking
 * for — "how many so far" — and reading it off a scale was the long way round.
 * Every other point keeps its value in the tooltip.
 */
const EndPoint = ({
  cx,
  cy,
  index,
  lastIndex,
  value,
}: {
  cx?: number;
  cy?: number;
  index?: number;
  lastIndex: number;
  value?: number;
}) => {
  if (index !== lastIndex || cx === undefined || cy === undefined) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill="hsl(var(--primary))" />
      <text
        x={cx - 6}
        y={cy - 10}
        textAnchor="end"
        fontSize={11}
        fontWeight={600}
        fill="hsl(var(--primary))"
      >
        {value}
      </text>
    </g>
  );
};

export const Chart: React.FC<ChartProps> = ({ period, current, previous, title }) => {
  const labels = periodLabels(period);
  const hasPreviousData = previous.quantity > 0;

  // Overlay the previous series onto the current series positionally (by index),
  // mirroring the original comparison chart behaviour.
  const data = current.values.map((point, index) => ({
    label: formatKey(point.key, period),
    current: point.value,
    previous: previous.values[index]?.value ?? null,
  }));

  // Five labels across the axis, as drawn. Recharts' own tick fitting put eight
  // dates under a 30-day range, which turned the axis into a second data series
  // to read. `interval` counts ticks skipped, so one less than the stride.
  const tickInterval = Math.max(0, Math.ceil(data.length / 5) - 1);

  return (
    <div>
      {/* The count belongs to the chart's own header as a comparison, not as a
          third big number competing with the stat cards above it. The legend
          sits here rather than under the plot, where it was pushing the last
          series line into the axis. */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
            {hasPreviousData
              ? `${current.quantity} this period · ${previous.quantity} previous`
              : `${current.quantity} this period`}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-0.5 w-3.5 rounded-full bg-primary" />
            {labels.current}
          </span>
          {hasPreviousData && (
            <span className="flex items-center gap-1.5">
              <span aria-hidden="true" className="h-0.5 w-3.5 rounded-full bg-muted-foreground/50" />
              {labels.previous}
            </span>
          )}
        </div>
      </div>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {/* Symmetric side margins because the axis labels are centred on
              their ticks: with none, half of the first date sat outside the
              SVG and "Jul 1" rendered as "l 1". */}
          <RechartsLineChart data={data} margin={{ top: 14, right: 18, left: 18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 4" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="label"
              interval={tickInterval}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
            />
            {/* Kept for the scale it gives the lines, hidden as the design
                draws it: the plot carries no value axis, and the figure a
                seller wants — where the line got to — is marked on the line
                itself. `width={0}` also reclaims the gutter, which is what
                let the axis labels crowd the first data point. */}
            <YAxis allowDecimals={false} tickCount={4} hide width={0} />
            <Tooltip
              contentStyle={{
                borderRadius: '0.5rem',
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
                fontSize: '12px',
                boxShadow: '0 4px 12px rgb(0 0 0 / 0.08)',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
            />
            <Line
              type="monotone"
              dataKey="current"
              name={labels.current}
              stroke="hsl(var(--primary))"
              strokeWidth={2.5}
              dot={(props) => (
                <EndPoint
                  key={props.key}
                  cx={props.cx}
                  cy={props.cy}
                  index={props.index}
                  lastIndex={data.length - 1}
                  value={current.quantity}
                />
              )}
              activeDot={{ r: 4 }}
            />
            {hasPreviousData && (
              // Solid, as drawn, and lighter than the current line. A dashed
              // comparison read as a projection rather than as last month.
              <Line
                type="monotone"
                dataKey="previous"
                name={labels.previous}
                stroke="hsl(var(--muted-foreground))"
                strokeOpacity={0.45}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
